import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { withRights } from 'providers/RightsConsumer';
import { Grid } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { addPropertyPrefix } from 'utils/propertyChangeAdapters';
import { SEARCH, TP_TEXT } from 'constants/inputTypes';
import { debounce } from 'lodash';

const OfferAssetPartnerTypeComponentAlpha = React.memo(
  ({
    loadPartners,
    loadContactInfo,
    partner,
    label,
    valueKey,
    id,
    validationInfo,
    rights,
    t,
    onPropertyChange,
    isSearchingRestrictedDealer,
    shouldUseUsersAsContactPersonsForDealer,
    processing,
    addToRefs
  }) => {
    const loadPartnersInternal = input => {
      if (!input) {
        return Promise.resolve([]);
      }

      return loadPartners(input);
    };

    const loadContactInfoInternal = input => {
      if (!input) {
        return Promise.resolve([]);
      }

      return loadContactInfo(input);
    };

    const contactInfo = partner && partner.contactInfo;
    const contactInfoValidationInfo = validationInfo.contactInfo || {};
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={6} id={label} ref={ref => addToRefs(ref, label, SEARCH)}>
          <Autocomplete
            id={'offerAssetCard_partnerCard_' + label.replace(/\s/g, '-').toLowerCase()}
            fullWidth
            cache={false}
            value={partner || {}}
            onChange={onPropertyChange(id)}
            label={label}
            valueKey={valueKey}
            error={Boolean(validationInfo[valueKey])}
            helperText={validationInfo[valueKey]}
            required={Boolean(valueKey)}
            loadOptions={debounce(loadPartnersInternal, 500, { leading: true })}
            disabled={rights.readOnlyFull || !rights.financedAssetDealerWrite || isSearchingRestrictedDealer || processing}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          id={`${label} ${t('OfferEdit.Asset.Partner.RegistrationCode')}`}
          ref={ref => addToRefs(ref, `${label} ${t('OfferEdit.Asset.Partner.RegistrationCode')}`, TP_TEXT)}
        >
          <TextField
            id={'offerAssetCard_partnerCard_' + label.replace(/\s/g, '-').toLowerCase() + '_registrationCode'}
            fullWidth
            value={(partner && partner.registrationCode) || ''}
            name='registrationCode'
            label={`${label} ${t('OfferEdit.Asset.Partner.RegistrationCode')}`}
            onChange={addPropertyPrefix(id, onPropertyChange)('registrationCode')}
            disabled={rights.readOnlyFull || !rights.financedAssetDealerWrite || isSearchingRestrictedDealer || processing}
            error={Boolean(validationInfo.registrationCode)}
            helperText={validationInfo.registrationCode}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Autocomplete
            id={'offerAssetCard_partnerCard_' + label.replace(/\s/g, '-').toLowerCase() + '_contactInfo'}
            fullWidth
            creatable={!shouldUseUsersAsContactPersonsForDealer}
            cache={false}
            disabled={rights.readOnlyFull || !rights.financedAssetDealerWrite || processing}
            value={contactInfo}
            onChange={addPropertyPrefix(id, onPropertyChange)('contactInfo')}
            label={`${label} ${t('OfferEdit.Asset.Partner.ContactPerson')}`}
            valueKey='contactInfoId'
            loadOptions={debounce(loadContactInfoInternal, 500, { leading: true })}
            error={Boolean(contactInfoValidationInfo.name)}
            helperText={contactInfoValidationInfo.name}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <TextField
            id={'offerAssetCard_partnerCard_' + label.replace(/\s/g, '-').toLowerCase() + '_phone'}
            fullWidth
            error={Boolean(contactInfoValidationInfo.phone)}
            helperText={contactInfoValidationInfo.phone}
            disabled={shouldUseUsersAsContactPersonsForDealer || contactInfo?.contactData}
            value={(contactInfo && contactInfo.phone) || ''}
            onChange={addPropertyPrefix(id, onPropertyChange)('contactInfo.phone')}
            label={`${label} ${t('OfferEdit.Asset.Partner.ContactPersonPhone')}`}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <TextField
            id={'offerAssetCard_partnerCard_' + label.replace(/\s/g, '-').toLowerCase() + '_email'}
            error={Boolean(contactInfoValidationInfo.email)}
            helperText={contactInfoValidationInfo.email}
            fullWidth
            disabled={shouldUseUsersAsContactPersonsForDealer || contactInfo?.contactData}
            value={(contactInfo && contactInfo.email) || ''}
            onChange={addPropertyPrefix(id, onPropertyChange)('contactInfo.email')}
            label={`${label} ${t('OfferEdit.Asset.Partner.ContactPersonEmail')}`}
          />
        </Grid>
      </Grid>
    );
  }
);

OfferAssetPartnerTypeComponentAlpha.displayName = 'OfferAssetPartnerTypeComponent';

OfferAssetPartnerTypeComponentAlpha.propTypes = {
  t: PropTypes.func.isRequired,
  partner: PropTypes.object,
  onPropertyChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  valueKey: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  loadPartners: PropTypes.func.isRequired,
  loadContactInfo: PropTypes.func.isRequired,
  validationInfo: PropTypes.object,
  rights: PropTypes.object.isRequired,
  isSearchingRestrictedDealer: PropTypes.bool,
  shouldUseUsersAsContactPersonsForDealer: PropTypes.bool.isRequired,
  processing: PropTypes.bool,
  addToRefs: PropTypes.func.isRequired
};

const MemoizedOfferAssetPartnerTypeComponentAlpha = compose(withTranslation())(withRights(OfferAssetPartnerTypeComponentAlpha));

const ForwardedOfferAssetPartnerTypeComponentAlpha = forwardRef((props, ref) => (
  <MemoizedOfferAssetPartnerTypeComponentAlpha {...props} forwardedRef={ref} />
));

ForwardedOfferAssetPartnerTypeComponentAlpha.displayName = 'ForwardedOfferAssetPartnerTypeComponentAlpha';

export default ForwardedOfferAssetPartnerTypeComponentAlpha;
